import React, { useState } from 'react';
import { Platform, TouchableOpacity, Text, View } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { StatusBar } from 'expo-status-bar';
import { Provider as PaperProvider, DefaultTheme } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';

// Import theme context
import { ThemeProvider, useTheme } from './src/contexts/ThemeContext';
import { ThemeSwitcher } from './src/components/ThemeSwitcher';
import { ErrorBoundary } from './src/components/ErrorBoundary';
import { MouseNavigationProvider } from './src/components/MouseNavigationProvider';
import { NavigationDebugger } from './src/components/NavigationDebugger';

// Import screens
import SplashScreen from './src/screens/SplashScreen';
import HomeScreen from './src/screens/HomeScreen';
import BarsScreen from './src/screens/BarsScreen';
import CocktailsScreen from './src/screens/CocktailsScreen';
import RumsScreen from './src/screens/RumsScreen';
import DistilleriesScreen from './src/screens/DistilleriesScreen';
import DistilleryDetailScreen from './src/screens/DistilleryDetailScreen';
import RumDetailScreen from './src/screens/RumDetailScreen';
import CocktailDetailScreen from './src/screens/CocktailDetailScreen';
import VenueDetailScreen from './src/screens/VenueDetailScreen';
import CultureScreen from './src/screens/CultureScreen';
import CulturalContentDetailScreen from './src/screens/CulturalContentDetailScreen';
import SearchScreen from './src/screens/SearchScreen';
import BlogScreen from './src/screens/BlogScreen';
import DatabaseTestScreen from './src/screens/DatabaseTestScreen';
import CritikiArchiveScreen from './src/screens/CritikiArchiveScreen';

// Create nested stack navigators for each tab
function BarsStack() {
  const { themeData } = useTheme();
  return (
    <Stack.Navigator>
      <Stack.Screen 
        name="BarsList" 
        component={BarsScreen} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="VenueDetail" 
        component={VenueDetailScreen} 
        options={{ 
          headerShown: true,
          title: 'Venue Details',
          headerStyle: {
            backgroundColor: themeData.colors.surface,
          },
          headerTintColor: themeData.colors.text,
        }}
      />
    </Stack.Navigator>
  );
}

function CocktailsStack() {
  const { themeData } = useTheme();
  return (
    <Stack.Navigator>
      <Stack.Screen 
        name="CocktailsList" 
        component={CocktailsScreen} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="CocktailDetail" 
        component={CocktailDetailScreen} 
        options={{ 
          headerShown: true,
          title: 'Cocktail Details',
          headerStyle: {
            backgroundColor: themeData.colors.surface,
          },
          headerTintColor: themeData.colors.text,
        }}
      />
    </Stack.Navigator>
  );
}

function RumsStack() {
  const { themeData } = useTheme();
  return (
    <Stack.Navigator>
      <Stack.Screen 
        name="RumsList" 
        component={RumsScreen} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="RumDetail" 
        component={RumDetailScreen as any} 
        options={{ 
          headerShown: true,
          title: 'Rum Details',
          headerStyle: {
            backgroundColor: themeData.colors.surface,
          },
          headerTintColor: themeData.colors.text,
        }}
      />
    </Stack.Navigator>
  );
}

function DistilleriesStack() {
  const { themeData } = useTheme();
  return (
    <Stack.Navigator>
      <Stack.Screen 
        name="DistilleriesList" 
        component={DistilleriesScreen} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="DistilleryDetail" 
        component={DistilleryDetailScreen} 
        options={{ 
          headerShown: true,
          title: 'Distillery Details',
          headerStyle: {
            backgroundColor: themeData.colors.surface,
          },
          headerTintColor: themeData.colors.text,
        }}
      />
    </Stack.Navigator>
  );
}

const Stack = createStackNavigator();
const Tab = createBottomTabNavigator();

// Theme switcher button component
const ThemeButton: React.FC = () => {
  const { themeData } = useTheme();
  const [showThemeSwitcher, setShowThemeSwitcher] = useState(false);

  return (
    <>
      <TouchableOpacity
        onPress={() => setShowThemeSwitcher(true)}
        style={{
          position: 'absolute',
          top: 50,
          right: 20,
          zIndex: 1000,
          backgroundColor: themeData.colors.primary,
          borderRadius: 25,
          width: 50,
          height: 50,
          justifyContent: 'center',
          alignItems: 'center',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 4,
          elevation: 5,
        }}
      >
        <Text style={{ fontSize: 20 }}>🎨</Text>
      </TouchableOpacity>
      
      <ThemeSwitcher
        visible={showThemeSwitcher}
        onClose={() => setShowThemeSwitcher(false)}
      />
    </>
  );
};

function TabNavigator() {
  const { themeData } = useTheme();

  return (
    <View style={{ flex: 1, backgroundColor: themeData.colors.background }}>
      <ThemeButton />
      <Tab.Navigator
        screenOptions={({ route }) => ({
          tabBarIcon: ({ focused, color, size }) => {
            let iconName: keyof typeof Ionicons.glyphMap;

            if (route.name === 'Home') {
              iconName = focused ? 'home' : 'home-outline';
            } else if (route.name === 'Tiki Bars') {
              iconName = focused ? 'beer' : 'beer-outline';
            } else if (route.name === 'Cocktails') {
              iconName = focused ? 'wine' : 'wine-outline';
            } else if (route.name === 'Rums') {
              iconName = focused ? 'flask' : 'flask-outline';
            } else if (route.name === 'Distilleries') {
              iconName = focused ? 'business' : 'business-outline';
            } else if (route.name === 'Culture') {
              iconName = focused ? 'library' : 'library-outline';
            } else if (route.name === 'Search') {
              iconName = focused ? 'search' : 'search-outline';
            } else if (route.name === 'Blog') {
              iconName = focused ? 'book' : 'book-outline';
            } else if (route.name === 'Critiki Archive') {
              iconName = focused ? 'archive' : 'archive-outline';
            } else if (route.name === 'DB Test') {
              iconName = focused ? 'flask' : 'flask-outline';
            } else {
              iconName = 'help-outline';
            }

            return <Ionicons name={iconName} size={size} color={color} />;
          },
          tabBarActiveTintColor: themeData.colors.primary,
          tabBarInactiveTintColor: themeData.colors.textSecondary,
          tabBarStyle: {
            backgroundColor: themeData.colors.surface,
            borderTopColor: themeData.colors.border,
            borderTopWidth: 1,
            elevation: 8,
            ...(Platform.OS === 'web' ? {
              boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.1)',
            } : {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: -2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
            }),
          },
          headerShown: false,
        })}
      >
        <Tab.Screen name="Home" component={HomeScreen} />
        <Tab.Screen name="Tiki Bars" component={BarsStack} />
        <Tab.Screen name="Cocktails" component={CocktailsStack} />
        <Tab.Screen name="Rums" component={RumsStack} />
        <Tab.Screen name="Distilleries" component={DistilleriesStack} />
        <Tab.Screen name="Culture" component={CultureScreen} />
        <Tab.Screen name="Blog" component={BlogScreen} />
        <Tab.Screen name="Critiki Archive" component={CritikiArchiveScreen} />
        <Tab.Screen name="DB Test" component={DatabaseTestScreen} />
      </Tab.Navigator>
    </View>
  );
}

function AppContent() {
  const { themeData } = useTheme();

  // Create dynamic theme for Paper Provider
  const paperTheme = {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      primary: themeData.colors.primary,
      secondary: themeData.colors.secondary,
      background: themeData.colors.background,
      surface: themeData.colors.surface,
      onSurface: themeData.colors.text,
      onSurfaceVariant: themeData.colors.textSecondary,
      outline: themeData.colors.border,
      error: themeData.colors.error,
    },
  };

  return (
    <PaperProvider theme={paperTheme}>
      <NavigationContainer>
        <MouseNavigationProvider>
          <StatusBar style="light" />
          <Stack.Navigator>
            <Stack.Screen 
              name="Splash" 
              component={SplashScreen} 
              options={{ headerShown: false }}
            />
            <Stack.Screen 
              name="App" 
              component={TabNavigator} 
              options={{ headerShown: false }}
            />

            <Stack.Screen 
              name="CulturalContentDetail" 
              component={CulturalContentDetailScreen as any} 
              options={{ 
                headerShown: false,
              }}
            />
          </Stack.Navigator>
          <NavigationDebugger />
        </MouseNavigationProvider>
      </NavigationContainer>
    </PaperProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </ErrorBoundary>
  );
} 